// Reusable JSON-LD schema builders for CaraLoom SEO (national marketplace).
import {
  SITE_URL,
  BRAND,
  TAGLINE,
  NAP,
  EMAIL,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from './siteConfig';

const SOCIAL = [
  'https://www.facebook.com/caraloom',
  'https://www.linkedin.com/company/caraloom',
  'https://twitter.com/caraloom',
];

const USA = { '@type': 'Country', name: 'United States' };

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  // MedicalOrganization is a subtype of Organization, so every existing
  // reference to `#organization` (WebSite publisher, BreadcrumbList,
  // Article publisher, Physician memberOf) continues to resolve correctly
  // while signalling healthcare-specific intent to Google + Bing + ChatGPT
  // browsing. Upgraded from plain Organization in Feb 2026.
  '@type': 'MedicalOrganization',
  '@id': `${SITE_URL}/#organization`,
  name: NAP.legalName,
  alternateName: BRAND,
  url: SITE_URL,
  logo: absoluteUrl('/brand/knot-badge-512.png'),
  image: DEFAULT_OG_IMAGE,
  description: TAGLINE,
  email: EMAIL,
  address: {
    '@type': 'PostalAddress',
    addressLocality: NAP.addressLocality,
    addressRegion: NAP.addressRegion,
    postalCode: NAP.postalCode,
    addressCountry: NAP.addressCountry,
  },
  areaServed: USA,
  sameAs: SOCIAL,
  // Healthcare-specific signals — each value is the schema.org MedicalSpecialty
  // enum form so the Rich Results Test parses them as typed entities (not
  // plain strings). These line up with the disciplines CaraLoom matches:
  // RN, LPN, OT, PT, SLP, NP, home-health aides, lactation consultants, etc.
  medicalSpecialty: [
    'PrimaryCare',
    'Geriatric',
    'Pediatric',
    'Nursing',
    'PhysicalTherapy',
    'OccupationalTherapy',
    'SpeechPathology',
    'Hospice',
    'Palliative',
    'Psychiatric',
    'Midwifery',
  ],
  // Concrete services we offer — points crawlers at the indexable
  // /services/* landing pages.
  availableService: [
    { '@type': 'MedicalProcedure', name: 'Private duty nursing',     url: absoluteUrl('/services/private-duty-nursing') },
    { '@type': 'MedicalProcedure', name: 'Skilled home nursing',     url: absoluteUrl('/services/skilled-nursing') },
    { '@type': 'MedicalProcedure', name: 'Post-surgery home recovery', url: absoluteUrl('/services/post-surgery-care') },
    { '@type': 'MedicalProcedure', name: 'Hospice support at home',  url: absoluteUrl('/services/hospice-support') },
    { '@type': 'MedicalProcedure', name: 'Dementia & Alzheimer\u2019s care', url: absoluteUrl('/services/dementia-alzheimers-care') },
    { '@type': 'MedicalProcedure', name: 'Home health aide services', url: absoluteUrl('/services/home-health-aide') },
    { '@type': 'MedicalProcedure', name: 'Companion care',           url: absoluteUrl('/services/companion-care') },
  ],
  // Provider audience — explicit MedicalAudience helps disambiguate
  // CaraLoom as a *marketplace for healthcare* (not a hospital itself).
  audience: { '@type': 'MedicalAudience', audienceType: 'Patients, family caregivers, and discharge planners' },
});

export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: BRAND,
  description: TAGLINE,
  publisher: { '@id': `${SITE_URL}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/marketplace?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const serviceSchema = ({ name, description, slug, areaName = 'United States' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${absoluteUrl(`/services/${slug}`)}#service`,
  serviceType: name,
  name: `${name} | ${BRAND}`,
  description,
  url: absoluteUrl(`/services/${slug}`),
  provider: { '@id': `${SITE_URL}/#organization` },
  areaServed: { '@type': 'Country', name: areaName },
  audience: { '@type': 'PatientsAudience', audienceType: 'Families and patients seeking in-home care' },
});

export const faqSchema = (faqs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

// items: [{ name, path }]
export const breadcrumbSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: absoluteUrl(it.path),
  })),
});

// Authority article (blog post) schema for the Resources hub.
// `path` (optional) overrides the default `/resources/{slug}` URL — used
// by /guides/{slug} (iter 174 knowledge center) so the JSON-LD @id and
// URLs point at the actual article surface.
export const articleSchema = ({ title, description, slug, image = DEFAULT_OG_IMAGE, datePublished, dateModified, path }) => {
  const articlePath = path || `/resources/${slug}`;
  return ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${absoluteUrl(articlePath)}#article`,
    headline: title,
    description,
    image,
    url: absoluteUrl(articlePath),
    datePublished,
    dateModified: dateModified || datePublished,
    author: { '@type': 'Organization', name: BRAND, url: SITE_URL },
    publisher: { '@id': `${SITE_URL}/#organization` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteUrl(articlePath) },
  });
};

// Public clinician profile schema (Physician + aggregateRating + reviews) for
// shareable, SEO-rich provider pages.
export const clinicianProfileSchema = (clinician, path, image = DEFAULT_OG_IMAGE) => {
  const s = {
    '@context': 'https://schema.org',
    '@type': 'Physician',
    '@id': `${absoluteUrl(path)}#clinician`,
    name: clinician.name,
    url: absoluteUrl(path),
    image,
    description: clinician.headline || clinician.bio || undefined,
    medicalSpecialty: (clinician.specialties || []).join(', ') || undefined,
    knowsLanguage: clinician.languages && clinician.languages.length ? clinician.languages : undefined,
    priceRange: clinician.hourly_rate ? `$${clinician.hourly_rate}/hr` : undefined,
    areaServed: clinician.city ? { '@type': 'City', name: clinician.city } : USA,
    address: clinician.city
      ? { '@type': 'PostalAddress', addressLocality: clinician.city, addressRegion: clinician.state, addressCountry: 'US' }
      : undefined,
    memberOf: { '@id': `${SITE_URL}/#organization` },
  };
  if (clinician.review_count > 0 && clinician.rating) {
    s.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: clinician.rating,
      reviewCount: clinician.review_count,
      bestRating: 5,
    };
  }
  if ((clinician.reviews || []).length > 0) {
    s.review = clinician.reviews.slice(0, 5).map((r) => ({
      '@type': 'Review',
      reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
      author: { '@type': 'Person', name: r.patient_name || 'Verified family' },
      reviewBody: r.comment,
    }));
  }
  Object.keys(s).forEach((k) => s[k] === undefined && delete s[k]);
  return s;
};

// Hyper-local service schema for city landing pages (Verified RN home care in {City}).
export const localServiceSchema = ({ city, state, path, description }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${absoluteUrl(path)}#service`,
  serviceType: 'In-home nursing and caregiving',
  name: `Verified Home Care in ${city}, ${state} | ${BRAND}`,
  description,
  url: absoluteUrl(path),
  provider: { '@id': `${SITE_URL}/#organization` },
  areaServed: { '@type': 'City', name: city, containedInPlace: { '@type': 'State', name: state } },
  audience: { '@type': 'PatientsAudience', audienceType: 'Families and patients seeking in-home care' },
});

// SoftwareApplication schema for pillar landing pages positioning CaraLoom as a
// post-acute care & discharge-planning platform. Used by /discharge-planning-software,
// /post-acute-care-platform, /care-transitions and future category pages.
// Do NOT invent ratings or user counts — leave them off unless we have real,
// citable numbers.
export const softwareApplicationSchema = ({
  name = BRAND,
  description,
  slug,
  applicationCategory = 'HealthApplication',
  applicationSubCategory = 'Discharge Planning & Care Coordination',
  operatingSystem = 'Web-based',
  featureList = [],
} = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': `${absoluteUrl(slug ? `/${slug}` : '/')}#software`,
  name,
  applicationCategory,
  applicationSubCategory,
  operatingSystem,
  description,
  url: absoluteUrl(slug ? `/${slug}` : '/'),
  publisher: { '@id': `${SITE_URL}/#organization` },
  audience: {
    '@type': 'MedicalAudience',
    audienceType: 'Discharge planners, case managers, hospitals, health systems, and post-acute providers',
  },
  featureList: featureList.length ? featureList : undefined,
  // Do not add offers/priceCurrency here — Google flags 'free' claims that
  // don't match reality. CaraLoom is B2B custom-priced.
});

